/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { PaymentService } from '../../services';
import { IconPaypal, IconMobilePayment } from '../../assets/icons';
import { classNames, Constants, formatAmount } from '../../utils';
import Render from '../../components/render';
import MobilePayment from './mobile-payment';

const RenewPlan = ({ onSuccess, onClose, initPayment }) => {
  const { countryId, form, handleSelectMonth, months, onSubmit, paymentMethods, payWithMobile, plan, selectedMonths, setPayWithMobile, total } = useForm(initPayment);

  const submit = async (paymentMethodId) => {
    const response = await onSubmit(paymentMethodId);
    if (response) onSuccess(response);
  }

  return (
    <>
      <Render condition={payWithMobile}>
        <MobilePayment
          type={Constants.PAYMENT_TYPE.RENEW}
          request={form}
          onClose={() => setPayWithMobile(false)}
          onSuccess={() => onSuccess()}
        />
      </Render>

      <Modal size="md" isOpen={true} onClose={onClose}>
        <ModalContent>
          <ModalHeader className="flex flex-col">Mis pagos</ModalHeader>
          <ModalBody className="relative">
            <p className='text-center capitalize font-bold text-lg mb-4'>{plan?.name}</p>
            <div className="flex items-center justify-between h-fit text-secondary mb-0">
              <p className='font-bold'>Fecha de pago</p>
              <p className='font-bold'>Monto</p>
            </div>
            <div className="flex flex-col gap-2 mt-0 border-b-1 border-secondary pb-4">
              {months.map((month, index) => {
                const isSelected = selectedMonths.includes(month.date);
                const isPaid = month.pay;
                const isNotValid = moment(month.date).isBefore(moment().startOf('day'));
                const isDisabled = isPaid || isNotValid;

                return (
                  <button
                    key={index}
                    onClick={() => handleSelectMonth(index)}
                    disabled={isDisabled}
                    className={`
                      flex justify-between items-center p-4 rounded-lg border transition
                      ${isDisabled ? 'bg-gray/20 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}
                      ${isSelected ? 'bg-green/20 border-green' : 'border-gray'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`
                          w-5 h-5 rounded-full border-2 flex items-center justify-center
                          ${isSelected ? 'border-green' : 'border-gray'}
                        `}
                      >
                        {isSelected && !isDisabled && (
                          <div className="w-2.5 h-2.5 rounded-full bg-green" />
                        )}
                        {isDisabled && (
                          <div className="w-2.5 h-2.5 rounded-full bg-gray" />
                        )}
                      </div>
                      <span className="text-sm font-medium">
                        {moment(month.date).locale('es').format('DD-MMM').toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-bold">
                      { formatAmount(plan?.price, '$') }
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between h-fit text-secondary mb-0">
              <p className="font-bold text-lg">Total</p>
              <p className="font-bold text-lg">${total}</p>
            </div>

            <p className="text-center font-semibold">Seleccione el método de pago</p>

            <Render condition={countryId === Constants.COUNTRIES.COL}>
              <div className="grid grid-cols-2 gap-4 my-4">
                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.CO_PAYPAL)}>
                  <PaymentButton
                    label="PayPal"
                    icon={IconPaypal}
                    className="bg-primary text-white"
                    isDisabled={selectedMonths.length === 0}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.CO_PAYPAL)}
                  />
                </Render>

                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.CO_MOBILE)}>
                  <PaymentButton
                    label="Pago Móvil"
                    icon={IconMobilePayment}
                    isDisabled={selectedMonths.length === 0}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.CO_MOBILE)}
                  />
                </Render>
              </div>
            </Render>

            <Render condition={countryId === Constants.COUNTRIES.VEN}>
              <div className="grid grid-cols-2 gap-4 my-4">
                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.VE_PAYPAL)}>
                  <PaymentButton
                    label="PayPal"
                    icon={IconPaypal}
                    className="bg-primary text-white"
                    isDisabled={selectedMonths.length === 0}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.VE_PAYPAL)}
                  />
                </Render>

                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.VE_MOBILE)}>
                  <PaymentButton
                    label="Pago Móvil"
                    icon={IconMobilePayment}
                    isDisabled={selectedMonths.length === 0}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.VE_MOBILE)}
                  />
                </Render>
              </div>
            </Render>

          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const PaymentButton = ({ label, icon, className, isDisabled, onSubmit }) => (
  <Button
    className={classNames('flex flex-1 justify-center items-center gap-2 border-primary pl-1', className)}
    variant="bordered"
    radius="sm"
    onPress={onSubmit}
    isDisabled={isDisabled}
  >
    <div className="bg-white p-[2px] rounded-sm flex items-center justify-cente">
      <img src={icon} alt="paypal" className="w-6 h-6" />
    </div>
    <p className="flex-1 font-semibold">{ label }</p>
  </Button>
)

const useForm = (initPayment) => {
  const user = useSelector((state) => state.user);
  const [form, setForm] = useState({
    plan_id: undefined,
    amount: 0,
    plan: null,
    user,
  });
  const [countryId, setCountryId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [total, setTotal] = useState(0);
  const [payWithMobile, setPayWithMobile] = useState(false);
  const location = useLocation();

  const getPlan = async () => {
    try {
      const response = await PaymentService.payment.getPlanUser({ user_id: user?.id })
      setPlan(response.plan);
      setMonths(response.months);
      setForm(s => ({
        ...s,
        plan_id: response.plan.id,
        amount: response.plan.price,
        plan: response.plan,
        user,
      }));

    } catch (error) {
      toast.error(String(error) || 'Ocurrió un error inesperado');
      return false;
    }
  }

  const getPaymentMethods = () => {
    PaymentService.payer.getMethods({ user_id: user.id })
      .then(res => {
        setCountryId(res[0].country_id);
        setPaymentMethods(res);
      })
      .catch(console.log);
  }

  const handleSelectMonth = (index) => {
    const selectedMonth = months[index];
    const isNotValid = moment(selectedMonth.date).isBefore(moment().startOf('day'));

    if (selectedMonth.pay || isNotValid) return;

    const selectedIndexInState = selectedMonths.findIndex(date => date === selectedMonth.date);
    const isSelected = selectedIndexInState !== -1;

    let newSelectedMonths = [...selectedMonths];

    if (isSelected) {
      if (selectedMonths[selectedMonths.length - 1] === selectedMonth.date) {
        newSelectedMonths = selectedMonths.slice(0, -1);
      } else {
        return;
      }
    } else {
      if (selectedMonths.length === 0) {
        const firstSelectableIndex = months.findIndex(m => !m.pay);
        if (index === firstSelectableIndex) {
          newSelectedMonths = [selectedMonth.date];
        } else {
          return;
        }
      } else {
        const lastSelectedDate = selectedMonths[selectedMonths.length - 1];
        const lastSelectedIndex = months.findIndex(m => m.date === lastSelectedDate);
        if (index === lastSelectedIndex + 1 && !selectedMonth.pay) {
          newSelectedMonths = [...selectedMonths, selectedMonth.date];
        } else {
          return;
        }
      }
    }

    setSelectedMonths(newSelectedMonths);
    setTotal((newSelectedMonths.length * plan.price).toFixed(2));
  }

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const submit = async (paymentMethodId) => {
    try {
      if (total === 0) {
        return toast.error('Debe seleccionar un plan');
      }

      const isValidMonths = await PaymentService.payment.validateMonths({
        user_id: user?.id,
        plan_id: plan.id,
        months: selectedMonths
      })

      if (!isValidMonths) {
        return toast.error('Los meses seleccionados no son válidos');
      }

      const mobilePayment = [Constants.PAYMENT_METHODS.CO_MOBILE, Constants.PAYMENT_METHODS.VE_MOBILE];
      const payPalPayment = [Constants.PAYMENT_METHODS.CO_PAYPAL, Constants.PAYMENT_METHODS.VE_PAYPAL];

      if (payPalPayment.includes(paymentMethodId)) {
        await payWithPayPal();

      } else if (mobilePayment.includes(paymentMethodId)) {
        setPayWithMobile(true);

      } else {
        return toast.error('Debe seleccionar un método de pago válido');
      }

    } catch (error) {
      toast.error(error || 'Ocurrió un error inesperado');
    }
  }

  const payWithPayPal = async () => {
    try {
      await initPayment(
        user,
        plan.id,
        +total,
        location.pathname,
        selectedMonths.length > 0 ? selectedMonths : null
      );

    } catch (error) {
      toast.error(error || 'Ocurrió un error inesperado');
    }
  }

  useEffect(() => {
    getPlan();
    getPaymentMethods();
  }, []);

  return {
    countryId,
    form,
    handleSelectMonth,
    months,
    onChange,
    onSubmit: submit,
    paymentMethods,
    payWithMobile,
    plan,
    selectedMonths,
    setPayWithMobile,
    total,
  }
}

export default RenewPlan;
